import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import {
    makeStyles,
    InputBase,
    ButtonBase,
    SwipeableDrawer,
} from '@material-ui/core';
import { optimoveEvents } from 'src/screens/optimove';
import COLORS from 'src/lib/colors';
import SearchResults from 'src/components/store-search-modal/search-results';
import NearbyResults from 'src/components/store-search-modal/nearby-results';
import useStoreSearch from 'src/hooks/useStoreSearch';
import clsx from 'clsx';
import NoStoreResults from 'src/components/store-search-modal/no-store-result';
import { useIsMobileWidth } from 'src/hooks/useWidth';
import { Store } from 'src/services/types';
import { useIsActiveScreen } from 'src/lib/contexts/screen-order-context';
import { SCREEN_NAMES } from 'src/controllers/config';
import eventMetricsMap from 'src/hooks/useEventsReporter/event-types';
import useEventsReporter from 'src/hooks/useEventsReporter';
import useRouter from 'src/hooks/useRouter';
import { Body } from '../UI';
import Button from '../UI/Button';
import { useGeolocation } from '../../hooks';

const useStyles = makeStyles({
    dialogEdge: {
        borderTopLeftRadius: '15px',
        borderTopRightRadius: '15px',
        width: '100%',
    },
    container: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        flexShrink: 1,
        padding: '0 21px',
        '& > span': {
            background: '#D9D9D9',
            width: '20%',
            height: '7px',
            position: 'absolute',
            borderRadius: '5px',
            top: '8px',
            left: 'calc(50% - 40px)',
        },
    },
    titleAndClose: {
        width: '100%',
        marginTop: '1rem',
    },
    titleText: {
        marginTop: '.5rem',
        // fontWeight: 'bold',
        // fontSize: '1.25rem',
        letterSpacing: '-0.34pt',
        width: '100%',
    },
    searchInputFormWrapper: {
        display: 'flex',
        width: '100%',
        flexShrink: 1,
        flexDirection: 'column',
    },
    searchInputForm: {
        width: '100%',
        display: 'contents',
    },
    searchInput: {
        fontFamily: 'StaplesNormsRegular',
        width: '100%',
        padding: '10px',
        fontSize: '16px',
        borderRadius: '10px !important',
        border: '1px solid rgb(204, 204, 204)',
        height: '50px',
        outline: 'none',
        boxShadow: 'none !important',
        marginTop: '10px',
    },
    searchIconBtn: {
        width: '2.15rem',
        height: '1.65rem',
        padding: '0 .25rem',
        marginLeft: '-.5rem',
        marginRight: '.25rem',
    },
    searchIcon: {
        width: '1.25rem',
        height: '1.25rem',
    },
    locationButton: {
        background: 'none !important',
        color: COLORS.darkGray,
        fontSize: '14px',
        fontFamily: 'StaplesNormsRegular',
        textDecoration: 'underline',
    },
    locationButtonSize: {
        fontSize: '14px',
    },
    paperDrawer: {
        borderRadius: ' 15px 15px 0px 0px',
        top: '8%',
    },
});

export default function StoreSearchModal({
    isLoading = false,
    open,
    itemNumber,
    withInventory,
    title = 'Change location',
    selectStoreCtaText = 'Set as my store',
    handleClose,
    mainStore,
    nearbyStores,
    onStoreSelected,
    ignoreIsActiveScreen,
    includeMap = true,
    reportEvents = {
        open: eventMetricsMap.change_location_tapped,
        search: eventMetricsMap.search_complete,
        selected: eventMetricsMap.store_set,
        close: eventMetricsMap.change_location_close,
    },
}: any) {
    const classes = useStyles();
    const [isSearching, setIsSearching] = useState(false);
    const [searchString, setSearchString] = useState('');
    const [submittedSearchString, setSubmittedSearchString] = useState('');
    const { customEvent, analyticsCustomEvent } = useEventsReporter();
    const { history, pathname } = useRouter();
    const [locationStores, setLocationStores] = useState<Store[]>([]);
    const InputRef = useRef<HTMLInputElement>();
    const {
        isFetching,
        placeholderStoresData,
        stores: storeResults,
        clearStores,
    } = useStoreSearch({
        partialAddress: submittedSearchString,
        enabled: submittedSearchString.length !== 0,
        withInventory,
        itemNumber,
    });

    function onUpdateSearch(
        event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
    ) {
        setSearchString(event.target.value);
    }

    useEffect(() => {
        setIsSearching(
            storeResults.length === 0 ||
                isFetching ||
                submittedSearchString !== ''
        );
    }, [storeResults.length, isFetching, submittedSearchString]);

    useEffect(() => {
        if (isSearching && !isFetching) {
            if (reportEvents?.search) {
                customEvent(reportEvents.search);
            }
        }
    }, [isSearching, isFetching]);

    function clear() {
        setSearchString('');
        setIsSearching(false);
        setSubmittedSearchString('');
        setLocationStores([]);
        clearStores();
    }

    function handleSubmit(e: any) {
        console.log('🚀 ~ handleSubmit ~ handleSubmit:');
        e.preventDefault();
        if (searchString !== submittedSearchString) {
            setLocationStores([]);
            clearStores();
            setSubmittedSearchString(searchString);
        }
        InputRef.current?.blur();
    }

    function onClose() {
        clear();
        if (reportEvents?.close) {
            customEvent(reportEvents.close);
        }
        handleClose();
    }

    useEffect(() => {
        if (!open) {
            setIsSearching(false);
        }
        if (open) {
            const unblock = history.block(() => {
                onClose();
                return false;
            });
            return () => {
                unblock();
            };
        }
        return undefined;
    }, [open]);

    function onEnter() {
        if (reportEvents?.open) {
            customEvent(reportEvents.open);
        }
    }

    function handleStoreSelected(newStore: Store) {
        if (reportEvents?.selected) {
            customEvent(reportEvents.selected);
            analyticsCustomEvent(eventMetricsMap.stores, {
                click_text: 'set as my store',
                element_location: 'change location',
                store_details: `${mainStore?.address?.city}-${mainStore?.storeNumber}`,
            });
            optimoveEvents(eventMetricsMap.scm_stores, {
                click_text: 'set as my store',
                element_location: 'change location',
                store_details: `${mainStore?.address?.city}-${mainStore?.storeNumber}`,
            });
        }
        clear();
        onStoreSelected(newStore);
    }

    const hasStores = !!nearbyStores?.length || !!mainStore;
    const showMap = includeMap && hasStores;
    const showNearbyResults = !isFetching && hasStores && !isLoading;
    const showNoStoreResults = !isFetching && !hasStores && !isLoading;
    const isMobileWidth = useIsMobileWidth();

    const isStoreSearchModalOpen = useIsActiveScreen(
        SCREEN_NAMES.CHANGE_LOCATION,
        open && !ignoreIsActiveScreen
    );

    const { detectAndSaveZipcode, isDetectingLocation } = useGeolocation();
    const showStoreResult = (stores: any) => {
        if (stores?.length) {
            setLocationStores(stores);
        } else if (stores?.length === 0) {
            console.log('No stores found within 250 miles of this zip code');
        }
    };

    return (
        <SwipeableDrawer
            anchor="bottom"
            open={ignoreIsActiveScreen ? open : isStoreSearchModalOpen}
            onClose={onClose}
            onOpen={onEnter}
            classes={{
                paper: classes.paperDrawer,
            }}
            style={{ borderRadius: ' 15px 15px 0px 0px' }}
            disableDiscovery
            disableSwipeToOpen
        >
            <div className={classes.container} style={{ padding: '1rem' }}>
                <span />
                <div className={classes.titleAndClose}>
                    <Body size={26} weight="bold" className={classes.titleText}>
                        {title}
                    </Body>
                </div>
                <div className={classes.searchInputFormWrapper}>
                    <form
                        className={classes.searchInputForm}
                        onSubmit={handleSubmit}
                    >
                        <InputBase
                            id="store-change-modal-search-input"
                            className={clsx(classes.searchInput, isSearching)}
                            placeholder="Search city, state, zip code"
                            onChange={onUpdateSearch}
                            value={searchString}
                            inputMode="search"
                            type="search"
                            inputRef={InputRef}
                            endAdornment={
                                <ButtonBase
                                    onClick={handleSubmit}
                                    id="store-change-modal-search-button"
                                    className={classes.searchIconBtn}
                                >
                                    <img
                                        id="store-change-modal-search-icon-img"
                                        src={require('../../assets/icons/iconProductSearch.svg')}
                                        className={classes.searchIcon}
                                        alt="search icon"
                                    />
                                </ButtonBase>
                            }
                        />
                    </form>
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <Button
                            className={classes.locationButton}
                            contentClassName={classes.locationButtonSize}
                            onClick={() => {
                                detectAndSaveZipcode({ showStoreResult });
                            }}
                        >
                            Use my current location
                        </Button>
                    </div>
                </div>

                {isSearching && storeResults && !locationStores.length && (
                    <SearchResults
                        withInventory={withInventory}
                        isFetching={isFetching}
                        results={storeResults}
                        placeholderStoreResults={placeholderStoresData}
                        onStoreSelected={handleStoreSelected}
                        selectStoreCtaText={selectStoreCtaText}
                    />
                )}

                {locationStores.length ? (
                    <SearchResults
                        withInventory={withInventory}
                        isFetching={isFetching}
                        results={locationStores}
                        placeholderStoreResults={placeholderStoresData}
                        onStoreSelected={handleStoreSelected}
                        selectStoreCtaText={selectStoreCtaText}
                    />
                ) : null}

                {!isSearching &&
                    showNearbyResults &&
                    !locationStores.length && (
                        <NearbyResults
                            withInventory={withInventory}
                            mainStore={mainStore}
                            nearbyStores={nearbyStores}
                            onStoreSelected={handleStoreSelected}
                            selectStoreCtaText={selectStoreCtaText}
                        />
                    )}
                {isLoading && <NoStoreResults subText="Loading..." />}
                {showNoStoreResults && (
                    <NoStoreResults subText="Please enter in a search criteria above and select your Staples store." />
                )}
            </div>
        </SwipeableDrawer>
    );
}
